import React, { useEffect, useState } from "react";
import { useT } from "../lib/i18n";
import { ShareNetwork, X, DeviceMobile } from "@phosphor-icons/react";

const STORAGE_KEY = "zcardon_ios_install_dismissed";

/**
 * One-time iOS install nudge.
 *
 * iOS Safari does NOT support the `beforeinstallprompt` event that Android
 * Chrome uses, so the only way to install on iPhone is the manual
 * Share → "Add to Home Screen" route. This component detects that path and
 * teaches the user — once. Dismissal is sticky (localStorage).
 *
 * It never shows when:
 *  - the app is already running standalone (`navigator.standalone` is true),
 *  - the device isn't iOS,
 *  - the user has already dismissed it,
 *  - the page is an in-app webview (Instagram / Facebook / etc.) — installing
 *    from those won't work anyway.
 */
function isIos() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || navigator.vendor || "";
    // Modern iPad reports "MacIntel" + touch — handle both.
    const iosUa = /iPad|iPhone|iPod/.test(ua);
    const iPadOS = /Mac/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
    return iosUa || iPadOS;
}

function isStandalone() {
    if (typeof window === "undefined") return false;
    // iOS Safari sets navigator.standalone; PWA-installed Chrome reports via media query.
    return Boolean(window.navigator.standalone) || window.matchMedia("(display-mode: standalone)").matches;
}

function isWebview() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    // Common in-app browsers we explicitly skip — their share sheet doesn't
    // include "Add to Home Screen".
    return /(FBAN|FBAV|Instagram|Line|Twitter|TikTok|MicroMessenger)/i.test(ua);
}

export default function IosInstallPrompt() {
    const t = useT();
    const [show, setShow] = useState(false);

    useEffect(() => {
        try {
            if (localStorage.getItem(STORAGE_KEY) === "1") return;
        } catch {
            return;
        }
        if (!isIos() || isStandalone() || isWebview()) return;
        // Wait a beat so it doesn't slam the very first paint.
        const t0 = setTimeout(() => setShow(true), 1500);
        return () => clearTimeout(t0);
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
        setShow(false);
    };

    if (!show) return null;

    return (
        <div
            className="fixed bottom-4 inset-x-4 z-[60] rounded-2xl bg-[#112318] text-[#FBF9F6] p-4 shadow-2xl border border-white/10 animate-in slide-in-from-bottom-4"
            data-testid="ios-install-prompt"
        >
            <div className="flex items-start gap-3">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-[#184A30] flex items-center justify-center">
                    <DeviceMobile size={22} weight="duotone" className="text-[#FBF9F6]" />
                </div>
                <div className="flex-1">
                    <p className="font-semibold text-sm">{t("ios_install.title")}</p>
                    <p className="text-[12px] text-white/70 mt-1 leading-relaxed">
                        {t("ios_install.step_1")}
                        <span className="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded bg-white/10">
                            <ShareNetwork size={12} weight="duotone" />
                            {t("ios_install.share_label")}
                        </span>
                        {t("ios_install.step_2")}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={dismiss}
                    className="shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors"
                    aria-label="Dismiss"
                    data-testid="ios-install-prompt-dismiss"
                >
                    <X size={18} weight="bold" />
                </button>
            </div>
        </div>
    );
}
